import {AfterViewInit, Component, Input, OnInit, inject} from '@angular/core';
import {MapService, MarkerData} from '../services/map.service';
import {MarkerClusterer} from '@googlemaps/markerclusterer';
import {NgStyle} from '@angular/common';
import {Router} from '@angular/router';

@Component({
    selector: 'app-map',
    imports: [NgStyle],
    template: `
        <div [id]="ID" [ngStyle]="{height: height, width: width}"></div>
    `
})
export class MapComponent implements OnInit, AfterViewInit {

    private mapService = inject(MapService);
    private router = inject(Router);

    @Input({required: true}) ID!: string;
    @Input() width = '100%';
    @Input() height = '500px';
    @Input() coordinates!: google.maps.LatLng[];
    @Input() zoom = 12;
    @Input() draggable = true;
    @Input() center: google.maps.LatLng | null = null;

    map!: google.maps.Map;
    bounds!: google.maps.LatLngBounds;
    markers: google.maps.marker.AdvancedMarkerElement[] = [];
    infoWindow!: google.maps.InfoWindow;
    markerClusterer!: MarkerClusterer;
    mapLoaded = false;
    markerData: MarkerData[] = [];

    ngOnInit(): void {
        this.bounds = new google.maps.LatLngBounds();
        this.infoWindow = new google.maps.InfoWindow({content: 'empty'});
    }

    ngAfterViewInit(): void {
        if (!this.center) {
            navigator.geolocation.getCurrentPosition(position => {
                this.center = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
                this.setupMap();
            }, () => {
                this.center = new google.maps.LatLng({lat: 47.7497231, lng: 13.5299573});
                this.setupMap();
            });
        } else {
            this.setupMap();
        }
    }

    setupMap(): void {
        const element = document.getElementById(this.ID);
        if (!element) return;

        this.map = new google.maps.Map(element, {
            mapId: this.ID,
            center: this.center,
            zoom: this.zoom,
            streetViewControl: false,
            gestureHandling: this.draggable ? 'auto' : 'none',
            maxZoom: 16
        });
        if (this.coordinates) {
            this.coordinates.forEach(coordinate => this.addMarker(coordinate));
        }

        setTimeout(() => {
            this.mapLoaded = true;
            if (this.markerData.length > 0) {
                this.addMarkers(this.markerData);
            }
        });
    }

    /**
     * Adds marker data.
     * Markers are created after ensuring the map has loaded.
     */
    addMarkerData(markerData: MarkerData[]): void {
        this.markerData = markerData;
        if (this.mapLoaded) {
            this.addMarkers(this.markerData);
        }
    }

    private addMarkers(markerData: MarkerData[]): void {
        markerData.forEach(data => {
            this.addMarker(data.latLng, data.infoWindow, data.icon, data.storeId);
        });

        this.map.fitBounds(this.bounds);
        this.markerClusterer = new MarkerClusterer({map: this.map, markers: this.markers});

        this.markerData = [];
    }

    private addMarker(latLng: google.maps.LatLng, infoWindow: string | null = null, icon?: string, storeId?: string): void {
        const markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
            map: this.map,
            position: latLng
        };

        if (icon) {
            const img = document.createElement('img');
            img.src = icon;
            img.style.width = '32px';
            img.style.height = '32px';
            markerOptions.content = img;
        }

        const marker = new google.maps.marker.AdvancedMarkerElement(markerOptions);

        if (infoWindow) {
            marker.addListener('click', () => {
                const content = `
                    <div style="color: black;">
                        <style>.gm-style .gm-style-iw-c button {filter: invert(1);}</style>
                        ${infoWindow}
                        ${storeId ? `<br><button id="turnover-btn-${storeId}" style="margin-top: 8px; padding: 4px 12px; background-color: #003ef8; color: white; border: none; border-radius: 4px; cursor: pointer;">Umsatz anzeigen</button>` : ''}
                    </div>
                `;
                this.infoWindow.setContent(content);
                this.infoWindow.open(this.map, marker);

                if (storeId) {
                    setTimeout(() => {
                        const button = document.getElementById(`turnover-btn-${storeId}`);
                        if (button) {
                            button.addEventListener('click', () => {
                                this.router.navigate(['/turnover'], { queryParams: { id_store: storeId } });
                            });
                        }
                    }, 0);
                }
            });
        }

        if (marker.position) this.bounds.extend(marker.position);
        this.markers.push(marker);
    }

}
